/**
 * Sibling to {@link ConfigureButton} rendered inside every
 * `withComponentRules` wrapper. Visible while config mode is on.
 *
 * Opens an inline modal listing the component's globally-active rules
 * with a per-rule `Checkbox`. Save writes the new inactive set to
 * `PUT /claimflow/component-rules/inactive`. Preservation rule: inactive
 * ids not shown in the modal (e.g. ids of rules no longer active) stay
 * in the submitted payload.
 */

import { useState, type MouseEvent } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import type { ToastData } from '@components/base/feedback';
import { Checkbox } from '@components/base/inputs';

import { useConfigMode } from './configModeStore';
import { useComponentRules } from './useComponentRules';
import { useInactiveRuleIdsFor } from './inactiveRulesContext';
import { useUpdateInactiveRules } from './useUpdateInactiveRules';

/**
 * `useToast` throws when no provider is mounted — which is fine in
 * production (the app root ships one) but breaks page-level tests that
 * do not wrap their render in a `ToastProvider`. This wrapper falls
 * back to a no-op when the context is missing so the HOC keeps
 * rendering harmlessly.
 */
function useOptionalToast() {
  try {
    return useToast();
  } catch {
    return {
      toast: (_options: Omit<ToastData, 'id'>) => '',
      dismiss: (_id: string) => {},
      dismissAll: () => {},
    };
  }
}

export interface ToggleRulesButtonProps {
  /** The registered component id this button edits rules for. */
  componentId: string;
}

export function ToggleRulesButton({ componentId }: ToggleRulesButtonProps) {
  const { enabled } = useConfigMode();
  const [isOpen, setIsOpen] = useState(false);
  const { rules, isLoading } = useComponentRules(componentId);
  const inactiveSet = useInactiveRuleIdsFor(componentId);
  const mutation = useUpdateInactiveRules();
  const { toast } = useOptionalToast();

  // Partial override map keyed by `ruleId`. Contains only entries the
  // user has explicitly toggled during the current modal session. Any
  // rule NOT in the map reads its live "active" status from the
  // tenant's inactive set — so checkboxes render correctly even if
  // rules finish loading after the modal opens. Cleared on every
  // open so prior unsaved edits never leak.
  const [selections, setSelections] = useState<Record<string, boolean>>({});

  if (!enabled) {
    return null;
  }

  const label = `Edit rules for ${componentId}`;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Stop propagation so a parent row's onClick doesn't fire. No
    // preventDefault — this button opens a modal, not a link.
    event.stopPropagation();
    setSelections({});
    setIsOpen(true);
  };

  // Resolve the current checkbox state for a rule — prefer the user's
  // unsaved override, fall back to the live inactive-set membership.
  const isActive = (ruleId: string): boolean =>
    selections[ruleId] ?? !inactiveSet.has(ruleId);

  const handleClose = () => {
    setIsOpen(false);
    mutation.reset();
  };

  const handleToggle = (ruleId: string) => {
    setSelections((prev) => ({
      ...prev,
      [ruleId]: !(prev[ruleId] ?? !inactiveSet.has(ruleId)),
    }));
  };

  const handleSave = () => {
    // Preservation rule: start from the CURRENT inactive set (which may
    // contain ids for rules no longer globally active) and only flip
    // ids that correspond to rules shown in the modal.
    const next = new Set<string>(inactiveSet);
    for (const rule of rules) {
      if (isActive(rule.id)) {
        next.delete(rule.id);
      } else {
        next.add(rule.id);
      }
    }
    mutation.mutate(
      { componentId, ruleIds: [...next] },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: 'Rules updated',
            message: `Rule activation saved for ${componentId}.`,
          });
          setIsOpen(false);
        },
      },
    );
  };

  const errorMessage =
    mutation.error instanceof Error
      ? mutation.error.message
      : mutation.error
        ? 'Failed to save rule activation.'
        : null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={label}
        title={label}
        className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-neutral-50 px-2 py-1 text-xs text-neutral-700 shadow-sm hover:bg-neutral-100"
      >
        <SlidersHorizontal className="w-3 h-3" aria-hidden="true" />
        <span>Rules</span>
      </button>
      {isOpen && (
        <Modal open={isOpen} onClose={handleClose} size="md">
          <Modal.Header title={`Rules — ${componentId}`} />
          <Modal.Body>
            {errorMessage && (
              <Alert variant="error" className="mb-4">
                {errorMessage}
              </Alert>
            )}
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : rules.length === 0 ? (
              <p className="text-sm text-secondary-600">
                No active rules registered for this component.
              </p>
            ) : (
              <ul className="space-y-3">
                {rules.map((rule) => (
                  <li key={rule.id} className="flex flex-col gap-1">
                    <Checkbox
                      label={rule.name}
                      checked={isActive(rule.id)}
                      onChange={() => {
                        handleToggle(rule.id);
                      }}
                    />
                    <span className="ml-[26px] text-xs text-secondary-500">
                      {rule.description || rule.nlInput || '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={mutation.isPending}
              disabled={mutation.isPending || isLoading}
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

